import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { forkJoin } from 'rxjs';
import { CrmAccount, CrmClient, CrmClientCommunication } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { CustomerService } from '../../customer/customer.service';
import { Party } from '../../party/party.model';
import { PartyService } from '../../party/party.service';
import { PartyType } from '../../party/party-type.enum';
import { SessionStorageService } from '../../services/session-storage.service';
import { PaymentTerm } from '../models/payment-term.model';
import { Products } from '../models/product.model';
import { OrdersService } from '../order.service';
import { ProductsService } from '../product.service';
import { CommercialPartySource, ExecutionDraftLaunchData, ExecutionDraftLine, ExecutionDraftPreview, ExecutionDraftPreviewRequest } from './execution-sales-order.model';

@Component({
  selector: 'app-execution-sales-order-wizard',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ButtonModule, CheckboxModule, DatePickerModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, MessageModule, ProgressSpinnerModule, SelectModule,
    TagModule, ToastModule
  ],
  providers: [MessageService],
  templateUrl: './execution-sales-order-wizard.component.html',
  styleUrls: ['./execution-sales-order-wizard.component.css']
})
export class ExecutionSalesOrderWizardComponent implements OnInit {
  readonly data: ExecutionDraftLaunchData;
  preview?: ExecutionDraftPreview;
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  contacts: CrmClientCommunication[] = [];
  legacyParties: Party[] = [];
  legacyContacts: Party[] = [];
  paymentTerms: PaymentTerm[] = [];
  products: Products[] = [];
  selected = new Set<string>();
  crmClientId?: number;
  crmAccountId?: number;
  crmClientContactId?: number;
  legacyMainParty?: Party;
  legacyCustomerId?: number;
  partySource: CommercialPartySource = 'CRM';
  private partySourceExplicit = false;
  paymentTermId?: number;
  expectedDate = new Date(Date.now() + 7 * 86400000);
  productOverrides: Record<string, { productId: number; variantId?: number | null }> = {};
  loading = true;
  saving = false;

  constructor(
    config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private orders: OrdersService,
    private productService: ProductsService,
    private crm: CrmService,
    private parties: PartyService,
    private customers: CustomerService,
    private session: SessionStorageService,
    private router: Router,
    private messages: MessageService
  ) { this.data = config.data as ExecutionDraftLaunchData; }

  ngOnInit(): void {
    forkJoin({
      clients: this.crm.getClients(this.data.owningCompanyId, ['CUSTOMER']),
      parties: this.parties.getParties(this.data.owningCompanyId, [PartyType.Company, PartyType.Customer], '', 0, 1000),
			terms: this.orders.getAllPaymentTerms(this.data.owningCompanyId),
      products: this.productService.getAllProductsByOwningCompany(this.data.owningCompanyId)
    }).subscribe({
      next: ({ clients, parties, terms, products }) => {
        this.clients = clients || []; this.paymentTerms = terms || [];
        this.legacyParties = parties?.content || [];
        this.products = (products || []).filter(product => product.productType === 'SERVICE' && !product.inventoryManaged);
        this.loadPreview();
      },
      error: () => { this.loading = false; this.error('Reference data could not be loaded.'); }
    });
  }

  loadPreview(): void {
    this.loading = true;
    const request = this.request();
    const call = this.data.orderId
      ? this.orders.previewExecutionImport(this.data.orderId, request)
      : this.orders.previewExecutionDraft(request);
    call.subscribe({
      next: preview => {
        this.preview = preview;
        this.applyPreviewParty(preview);
        this.paymentTermId = this.paymentTermId || preview.resolvedPaymentTermId || this.defaultPaymentTerm();
        if (preview.expectedDate) this.expectedDate = new Date(preview.expectedDate);
        this.selected = new Set(preview.lines.filter(line => line.selectable && line.selectedByDefault).map(line => line.sourceKey));
        this.loading = false;
      },
      error: error => { this.loading = false; this.error(error?.error?.message || error?.error?.detail || 'Preview could not be generated.'); }
    });
  }

  selectPartySource(source: CommercialPartySource): void {
    this.partySource = source;
    this.partySourceExplicit = true;
    if (source === 'CRM') {
      this.legacyMainParty = undefined; this.legacyCustomerId = undefined; this.legacyContacts = [];
    } else {
      this.crmClientId = undefined; this.crmAccountId = undefined; this.crmClientContactId = undefined;
      this.accounts = []; this.contacts = [];
    }
    this.loadPreview();
  }

  onCrmClientChange(): void {
    this.partySource = 'CRM'; this.partySourceExplicit = true;
    this.crmAccountId = undefined; this.crmClientContactId = undefined;
    this.loadCrmOptions(this.crmClientId);
    this.paymentTermId = this.defaultPaymentTerm() || this.paymentTermId;
    this.loadPreview();
  }

  onLegacyPartyChange(): void {
    this.partySource = 'LEGACY'; this.partySourceExplicit = true;
    this.legacyCustomerId = this.legacyMainParty?.id.entityType === PartyType.Customer
      ? this.legacyMainParty.id.entityId : undefined;
    this.legacyContacts = [];
    if (this.legacyMainParty?.id.entityType === PartyType.Company) {
      this.customers.getCustomersByCompany(this.legacyMainParty.id.entityId).subscribe(customers => {
        this.legacyContacts = (customers || []).map(customer =>
          this.parties.convertToParty(customer, PartyType.Customer, this.data.owningCompanyId));
      });
    }
    this.loadPreview();
  }

  toggle(line: ExecutionDraftLine, checked: boolean): void {
    checked ? this.selected.add(line.sourceKey) : this.selected.delete(line.sourceKey);
  }

  mapProduct(line: ExecutionDraftLine, rawProductId: string | number | null | undefined): void {
    const productId = Number(rawProductId);
    if (!productId) { delete this.productOverrides[line.sourceKey]; return; }
    const product = this.products.find(item => item.productID === productId);
    this.productOverrides[line.sourceKey] = { productId, variantId: product?.productVariants?.length === 1 ? product.productVariants[0].variantID : null };
    this.loadPreview();
  }

  submit(): void {
    if (!this.preview || !this.hasResolvedParty() || !this.paymentTermId || !this.expectedDate || !this.selected.size) {
      this.error('Select a customer, payment term, expected date, and at least one eligible charge.'); return;
    }
    this.saving = true;
    const request = {
      ...this.request(), selectedSourceKeys: Array.from(this.selected), freshnessHash: this.preview.freshnessHash,
      actorId: this.session.getCustomer()?.id || this.session.getUser()?.id
    } as any;
    if (this.data.orderId) {
      this.orders.importExecutionLines(this.data.orderId, request).subscribe({
        next: result => this.complete(result, result.orderID || this.data.orderId),
        error: (error: any) => this.fail(error)
      });
    } else {
      this.orders.createExecutionDraft(request).subscribe({
        next: result => this.complete(result, result.salesOrders?.[0]?.orderID || result.existingDrafts?.[0]?.orderId),
        error: (error: any) => this.fail(error)
      });
    }
  }

  openExisting(orderId: number): void { this.ref.close(); this.router.navigate(['/order/sales-order/edit', orderId]); }
  isSelected(key: string): boolean { return this.selected.has(key); }
  productLabel(line: ExecutionDraftLine): string { return [line.productCode, line.productName, line.variantName].filter(Boolean).join(' · ') || 'Mapping required'; }
  trackLine(_: number, line: ExecutionDraftLine): string { return line.sourceKey; }

  private request(): ExecutionDraftPreviewRequest {
    return { owningCompanyId: this.data.owningCompanyId, sourceType: this.data.sourceType, sourceId: this.data.sourceId,
      partySource: this.partySourceExplicit ? this.partySource : undefined,
      crmClientId: this.partySource === 'CRM' ? this.crmClientId : undefined,
      crmAccountId: this.partySource === 'CRM' ? this.crmAccountId : undefined,
      crmClientContactId: this.partySource === 'CRM' ? this.crmClientContactId : undefined,
      legacyCompanyId: this.partySource === 'LEGACY' && this.legacyMainParty?.id.entityType === PartyType.Company
        ? this.legacyMainParty.id.entityId : undefined,
      legacyCustomerId: this.partySource === 'LEGACY' ? this.legacyCustomerId : undefined,
      paymentTermId: this.paymentTermId,
      expectedDate: this.expectedDate ? this.dateAtNoon(this.expectedDate) : undefined,
      productOverrides: this.productOverrides };
  }
  private defaultPaymentTerm(): number | undefined {
    return this.clients.find(client => client.id === this.crmClientId)?.defaultPaymentTermId || this.paymentTerms[0]?.paymentTermID;
  }
  private hasResolvedParty(): boolean {
    return !!this.preview?.resolvedCommercialParty || !!this.preview?.resolvedCrmClientId
      || !!this.preview?.resolvedLegacyCompanyId || !!this.preview?.resolvedLegacyCustomerId;
  }
  private applyPreviewParty(preview: ExecutionDraftPreview): void {
    if (preview.resolvedPartySource === 'CRM' && preview.resolvedCrmClientId) {
      this.partySource = 'CRM';
      const changed = this.crmClientId !== preview.resolvedCrmClientId;
      this.crmClientId = preview.resolvedCrmClientId;
      this.crmAccountId = this.crmAccountId || preview.resolvedCrmAccountId;
      this.crmClientContactId = this.crmClientContactId || preview.resolvedCrmClientContactId;
      if (changed || !this.accounts.length && !this.contacts.length) this.loadCrmOptions(this.crmClientId);
      return;
    }
    if (preview.resolvedPartySource === 'LEGACY') {
      this.partySource = 'LEGACY';
      const mainId = preview.resolvedLegacyCompanyId || preview.resolvedLegacyCustomerId;
      const mainType = preview.resolvedLegacyCompanyId ? PartyType.Company : PartyType.Customer;
      this.legacyMainParty = this.legacyParties.find(party =>
        party.id.entityId === mainId && party.id.entityType === mainType);
      this.legacyCustomerId = preview.resolvedLegacyCustomerId;
      if (this.legacyMainParty?.id.entityType === PartyType.Company) this.loadLegacyContacts(this.legacyMainParty.id.entityId);
    }
  }
  private loadCrmOptions(clientId?: number): void {
    if (!clientId) { this.accounts = []; this.contacts = []; return; }
    forkJoin({
      accounts: this.crm.getAccounts(this.data.owningCompanyId, clientId),
      contacts: this.crm.getClientMasterCommunications(clientId, this.data.owningCompanyId)
    }).subscribe(({ accounts, contacts }) => { this.accounts = accounts || []; this.contacts = contacts || []; });
  }
  private loadLegacyContacts(companyId: number): void {
    this.customers.getCustomersByCompany(companyId).subscribe(customers => {
      this.legacyContacts = (customers || []).map(customer =>
        this.parties.convertToParty(customer, PartyType.Customer, this.data.owningCompanyId));
    });
  }
  private dateAtNoon(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12, 0, 0);
  }
  private complete(result: unknown, orderId?: number): void {
    this.saving = false; this.ref.close(result);
    if (orderId) this.router.navigate(['/order/sales-order/edit', orderId]);
  }
  private fail(error: any): void {
    this.saving = false; this.error(error?.error?.message || error?.error?.detail || 'Sales order draft could not be created.');
  }
  private error(detail: string): void { this.messages.add({ severity: 'error', summary: 'Execution billing', detail }); }
}
